/**
 * atlasMap — Sprint 4B
 * Read-only geometry + aggregation for the interactive atlas booklet (Europe →
 * country → city). Pure, offline, isolated: it never writes trips or app state.
 *
 * The Europe overview is rendered as inline SVG from a bundled, simplified
 * GeoJSON (public/atlas/europe.geojson) — NO tiles, NO Leaflet — over a cream
 * canvas. Each country is filled along a single navy hue by its VISITED FRACTION
 * = visited cities / cities in the catalogue (cities-index), so the gradient is
 * the progress (no bar, no %).
 *
 * The city level (handled by the page component) reuses the itinerary's Leaflet
 * map and may use tiles.
 */
import storageManager from './storageManager';

// ── Constants (confirmar por Érico) ─────────────────────────────────────────
export const ATLAS_BASEMAP = 'none';          // overview without tiles: polygons on cream
export const ATLAS_FILL = 'sequential-navy';   // one hue deepened by fraction (not distinct colours)
export const ATLAS_NAV = 'tap-then-buttons';   // Phase A: tap to drill + lateral/page-number buttons (no swipe)
export const ATLAS_SWIPE_SURFACE = 'buttons';  // swipe disabled — buttons instead (avoids Leaflet drag conflict)

const norm = (s) => String(s || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();

// App country name → GeoJSON feature NAME (only where they differ).
const COUNTRY_ALIASES = {
  'england': 'United Kingdom',
  'moldova': 'Republic of Moldova',
  'north macedonia': 'The former Yugoslav Republic of Macedonia',
  'vatican city': 'Holy See (Vatican City)',
};
const geoNameForCountry = (country) => COUNTRY_ALIASES[norm(country)] || country;

// ── Bundled GeoJSON (cached) ────────────────────────────────────────────────
let _geoPromise = null;
export function loadEuropeGeo() {
  if (!_geoPromise) {
    _geoPromise = fetch('./atlas/europe.geojson')
      .then(r => r.ok ? r.json() : { type: 'FeatureCollection', features: [] })
      .catch(() => ({ type: 'FeatureCollection', features: [] }));
  }
  return _geoPromise;
}

// ── Catalogue + visited aggregation ─────────────────────────────────────────
let _catalogPromise = null;
/** country (display) → total cities in the catalogue. */
function loadCatalogTotals() {
  if (!_catalogPromise) {
    _catalogPromise = fetch('./TRIP/cities-index.json')
      .then(r => r.ok ? r.json() : { cities: [] })
      .then(idx => {
        const totals = new Map();   // normCountry → { country, total }
        const byCountry = new Map(); // normCountry → [cityNames]
        for (const c of (idx.cities || [])) {
          const k = norm(c.country);
          if (!totals.has(k)) { totals.set(k, { country: c.country, total: 0 }); byCountry.set(k, []); }
          totals.get(k).total++;
          byCountry.get(k).push(c.city);
        }
        return { totals, byCountry };
      })
      .catch(() => ({ totals: new Map(), byCountry: new Map() }));
  }
  return _catalogPromise;
}

/** Sealed trips only — consistent with the atlas (where you've been). */
async function loadSealedTrips() {
  const all = await storageManager.getAllTrips().catch(() => []);
  return all.filter(t => t && t.sealedAt);
}

/** normCountry → { country, cities:Set<normCity> } visited (sealed). */
async function visitedByCountry() {
  const trips = await loadSealedTrips();
  const m = new Map();
  for (const t of trips) {
    const country = t.destination?.country, city = t.destination?.city;
    if (!country || !city) continue;
    const k = norm(country);
    if (!m.has(k)) m.set(k, { country, cities: new Set() });
    m.get(k).cities.add(norm(city));
  }
  return m;
}

// ── Projection (equirectangular, latitude-corrected) ────────────────────────
const LAT0 = 52;
const KX = Math.cos(LAT0 * Math.PI / 180);
const projRaw = (lon, lat) => [lon * KX, -lat];

function eachCoord(geometry, fn) {
  const walk = (c) => Array.isArray(c[0]) ? c.forEach(walk) : fn(c[0], c[1]);
  walk(geometry.coordinates);
}

/** Build a projector that fits the given geometries into a width×? viewBox. */
function makeProjector(geometries, width = 1000, pad = 24) {
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  for (const g of geometries) eachCoord(g, (lon, lat) => {
    const [x, y] = projRaw(lon, lat);
    if (x < minX) minX = x; if (x > maxX) maxX = x;
    if (y < minY) minY = y; if (y > maxY) maxY = y;
  });
  if (!isFinite(minX)) { minX = 0; minY = 0; maxX = 1; maxY = 1; }
  const spanX = maxX - minX || 1, spanY = maxY - minY || 1;
  const innerW = width - pad * 2;
  const scale = innerW / spanX;
  const innerH = spanY * scale;
  const height = innerH + pad * 2;
  const project = (lon, lat) => {
    const [x, y] = projRaw(lon, lat);
    return [pad + (x - minX) * scale, pad + (y - minY) * scale];
  };
  return { project, viewBox: `0 0 ${Math.round(width)} ${Math.round(height)}`, width, height };
}

/** Geometry (Polygon/MultiPolygon) → SVG path 'd' using a projector. */
export function geometryToPath(geometry, project) {
  if (!geometry) return '';
  const ring = (r) => r.map(([lon, lat], i) => {
    const [x, y] = project(lon, lat);
    return `${i === 0 ? 'M' : 'L'}${x.toFixed(1)} ${y.toFixed(1)}`;
  }).join(' ') + ' Z';
  const poly = (p) => p.map(ring).join(' ');
  if (geometry.type === 'Polygon') return poly(geometry.coordinates);
  if (geometry.type === 'MultiPolygon') return geometry.coordinates.map(poly).join(' ');
  return '';
}

// ── Fill ramp: one navy hue deepened by fraction ────────────────────────────
/** fraction 0..1 → colour. 0 (or null) = faint border-only; →1 = deep navy. */
export function fractionFill(fraction) {
  if (fraction == null) return null;            // not in catalogue → context only
  if (fraction <= 0) return '#EDE4D3';          // visited 0 cities → pale cream tile
  // Interpolate creamDeep → blue → blueOcean (navy) in RGB.
  const stops = [
    [0.0, [0xC8, 0xDC, 0xE5]], // bluePale
    [0.5, [0x5B, 0x8F, 0xA8]], // blue
    [1.0, [0x1A, 0x33, 0x40]], // blueOcean (navy)
  ];
  let a = stops[0], b = stops[stops.length - 1];
  for (let i = 0; i < stops.length - 1; i++) {
    if (fraction >= stops[i][0] && fraction <= stops[i + 1][0]) { a = stops[i]; b = stops[i + 1]; break; }
  }
  const t = (fraction - a[0]) / ((b[0] - a[0]) || 1);
  const mix = (i) => Math.round(a[1][i] + (b[1][i] - a[1][i]) * t);
  return `rgb(${mix(0)}, ${mix(1)}, ${mix(2)})`;
}

// ── Page 1 · Europe overview ────────────────────────────────────────────────
/**
 * @returns {Promise<{ viewBox, features:[{name, appCountry, fraction, visited, d}],
 *   visitedCountries:number, visitedCities:number }>}
 */
export async function buildEuropeView() {
  const [geo, { totals }, visited] = await Promise.all([
    loadEuropeGeo(), loadCatalogTotals(), visitedByCountry(),
  ]);

  // Reverse lookup: geoName(norm) → app country in the catalogue.
  const geoToApp = new Map();
  for (const { country } of totals.values()) {
    geoToApp.set(norm(geoNameForCountry(country)), country);
  }

  const projector = makeProjector(geo.features.map(f => f.geometry));
  const features = geo.features.map(f => {
    const appCountry = geoToApp.get(norm(f.properties.NAME)) || null;
    let fraction = null, visitedFlag = false;
    if (appCountry) {
      const total = totals.get(norm(appCountry))?.total || 0;
      const v = visited.get(norm(appCountry))?.cities.size || 0;
      fraction = total > 0 ? Math.min(1, v / total) : 0;
      visitedFlag = v > 0;
    }
    return {
      name: f.properties.NAME,
      appCountry,
      fraction,
      visited: visitedFlag,
      d: geometryToPath(f.geometry, projector.project),
    };
  });

  let visitedCountries = 0, visitedCities = 0;
  for (const v of visited.values()) { if (v.cities.size > 0) { visitedCountries++; visitedCities += v.cities.size; } }

  return { viewBox: projector.viewBox, features, visitedCountries, visitedCities };
}

// ── Page 2 · Country drill-down ─────────────────────────────────────────────
/**
 * Fit to the country polygon and place its catalogue cities (visited/unvisited).
 * City centres come from the country POI file (the catalogue has no coords).
 * @returns {Promise<{ viewBox, countryD, cities:[{city, visited, x, y}] }|null>}
 */
export async function buildCountryView(appCountry) {
  const [geo, { byCountry }, visited] = await Promise.all([
    loadEuropeGeo(), loadCatalogTotals(), visitedByCountry(),
  ]);
  const feature = geo.features.find(f => norm(f.properties.NAME) === norm(geoNameForCountry(appCountry)));
  if (!feature) return null;

  const projector = makeProjector([feature.geometry], 1000, 40);
  const countryD = geometryToPath(feature.geometry, projector.project);

  const catalogCities = byCountry.get(norm(appCountry)) || [];
  const visitedSet = visited.get(norm(appCountry))?.cities || new Set();
  const centres = await loadCountryCityCenters(appCountry);
  const byCity = new Map(centres.map(c => [norm(c.city), c]));

  const cities = [];
  for (const city of catalogCities) {
    const centre = byCity.get(norm(city));
    if (!centre) continue; // no coords for this city → no marker
    const [x, y] = projector.project(centre.lng, centre.lat);
    cities.push({ city, visited: visitedSet.has(norm(city)), x, y });
  }

  return { viewBox: projector.viewBox, countryD, cities };
}

// ── City centres from the country POI file (median of a city's POIs) ────────
import i18n from '../i18n/index.js';
import { getCountryFile } from './countryUtils';
import loadTripPois, { loadCityHiddenGems } from './loadTripPois';
import { poiSlug } from './reveal';
import { getUnlockedPostcards } from './postcardManager';

const slimCity = (s) => norm(s);
const langFolder = () => {
  const l = (i18n.language || 'en').split('-')[0];
  return l === 'es' ? 'es/' : l === 'fr' ? 'fr/' : 'en/';
};
const median = (arr) => { const s = [...arr].sort((a, b) => a - b); const m = Math.floor(s.length / 2); return s.length % 2 ? s[m] : (s[m - 1] + s[m]) / 2; };

// ── Page 3 · City (reused itinerary Leaflet map; data only here) ────────────

/** Union of revealed (visited sites) + surfaced (found gems) ids across trips. */
async function unionRevealedSurfaced() {
  const all = await storageManager.getAllTrips().catch(() => []);
  const revealed = new Set(), surfaced = new Set();
  for (const t of all) {
    for (const id of (t?.revealedPois || [])) revealed.add(id);
    for (const id of (t?.surfacedPois || [])) surfaced.add(id);
  }
  return { revealed, surfaced };
}

/**
 * City-level data for the Leaflet page: visited sites, found gems (only those
 * the traveller surfaced — never spoiling the unfound), and whether the postcard
 * is unlocked. All cross-trip and read-only.
 * @returns {Promise<{ center:{lat,lng}|null, sites:[{name,lat,lng,visited}],
 *   gems:[{name,lat,lng}], postcardUnlocked:boolean }>}
 */
export async function buildCityView(country, city) {
  const [pois, cityGems, { revealed, surfaced }, postcards] = await Promise.all([
    loadTripPois({ destination: { country, city } }).catch(() => []),
    loadCityHiddenGems(country, city).catch(() => []),
    unionRevealedSurfaced(),
    getUnlockedPostcards().catch(() => []),
  ]);

  const cityGemIds = new Set(cityGems.map(g => g.id));
  const sites = [], gems = [];
  for (const p of pois) {
    if (typeof p.lat !== 'number' || typeof p.lng !== 'number') continue;
    if (p.hidden) {
      if (cityGemIds.has(p.id) && surfaced.has(p.id)) gems.push({ name: p.name, lat: p.lat, lng: p.lng });
    } else {
      sites.push({ name: p.name, lat: p.lat, lng: p.lng, visited: revealed.has(p.id) });
    }
  }

  const all = [...sites, ...gems];
  const center = all.length
    ? { lat: median(all.map(p => p.lat)), lng: median(all.map(p => p.lng)) }
    : null;
  const postcardUnlocked = postcards.some(c => c.cityId === poiSlug(city));

  return { center, sites, gems, postcardUnlocked };
}

async function loadCountryCityCenters(appCountry) {
  try {
    const fileId = await getCountryFile(appCountry);
    if (!fileId) return [];
    const res = await fetch(`./TRIP/${langFolder()}${fileId}.json`).catch(() => null);
    if (!res || !res.ok) return [];
    const data = await res.json();
    const out = [];
    for (const cityData of (data.cities || [])) {
      const pts = [];
      for (const key of ['culturalAttractions', 'monuments', 'localActivities', 'traditionalLocals']) {
        for (const p of (cityData[key] || [])) {
          if (typeof p.lat === 'number' && typeof p.lng === 'number') pts.push(p);
        }
      }
      if (pts.length === 0) continue;
      out.push({ city: cityData.city, lat: median(pts.map(p => p.lat)), lng: median(pts.map(p => p.lng)) });
    }
    return out;
  } catch { return []; }
}
